/**
 * One-time "Confirm your details" popup shown right after customer login.
 *
 * Purpose: all-hands re-gather of invoice-delivery info. The popup appears
 * on every login until the customer hits Save (which sets
 * details_confirmed_at on the account) — after that it never shows again.
 * "Not now" snoozes only for the current page session, so customers who
 * skip it are asked again at their next login. New customers get it on
 * their first sign-in.
 *
 * Sends NO emails/SMS — it only stores the details on the account.
 */
import { useState } from "react";
import { X, ClipboardCheck, Loader2 } from "lucide-react";
import { portalApi } from "@/lib/api";

const inputCls =
  "w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]";
const labelCls =
  "block text-[11px] uppercase tracking-wide text-[#4B5563] mb-1";

export default function ConfirmDetailsModal({
  accountId,
  serviceAddress,
  initial,
  primaryPhone,
  primaryEmail,
  t,
  onSnooze,
  onSaved,
}) {
  const [draft, setDraft] = useState({
    mailing_address: initial?.mailing_address || serviceAddress || "",
    phone: initial?.phone || primaryPhone || "",
    billing_email: initial?.billing_email || primaryEmail || "",
    // Point-of-contact phone starts as the invoice phone, but the
    // customer can change it independently.
    point_of_contact_phone:
      initial?.point_of_contact_phone || initial?.phone || primaryPhone || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await portalApi.updateContactInfo(accountId, {
        ...draft,
        details_confirmed: true,
      });
      onSaved?.(data.customer_contact_info);
    } catch (err) {
      setError(err?.detail || "Unable to save. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="confirm-details-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onSnooze();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#EDE5D4] px-5 py-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-[#0B1930]" />
            <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
              {t.confirmTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={onSnooze}
            disabled={busy}
            aria-label={t.confirmLater}
            className="text-[#4B5563] transition-colors hover:text-[#0B1930] disabled:opacity-40"
            data-testid="confirm-details-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-sm leading-relaxed text-[#4B5563]">
            {t.confirmBody}
          </p>
          <p className="mt-1.5 text-[11px] text-[#9CA3AF]">
            {t.confirmOneTime}
          </p>

          {error && (
            <div
              className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              data-testid="confirm-details-error"
            >
              {error}
            </div>
          )}

          <div className="mt-4 space-y-4">
            <div>
              <span className={labelCls}>{t.serviceAddress}</span>
              <div className="rounded-xl bg-[#F5F1EA] px-3 py-2 text-sm font-semibold text-[#0B1930]">
                {serviceAddress || "—"}
              </div>
              <p className="mt-1 text-[10px] text-[#9CA3AF]">
                {t.contactInfoAddressLocked}
              </p>
            </div>

            <div>
              <label className={labelCls} htmlFor="confirm-mailing">
                {t.confirmMailing}
              </label>
              <input
                id="confirm-mailing"
                type="text"
                value={draft.mailing_address}
                onChange={set("mailing_address")}
                maxLength={200}
                placeholder="Street, City, State ZIP"
                className={inputCls}
                data-testid="confirm-details-mailing-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="confirm-phone">
                {t.confirmInvoicePhone}
              </label>
              <input
                id="confirm-phone"
                type="tel"
                value={draft.phone}
                onChange={set("phone")}
                placeholder="(407) 555-0101"
                className={inputCls}
                data-testid="confirm-details-phone-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="confirm-email">
                {t.confirmInvoiceEmail}
              </label>
              <input
                id="confirm-email"
                type="email"
                value={draft.billing_email}
                onChange={set("billing_email")}
                placeholder="you@example.com"
                className={inputCls}
                data-testid="confirm-details-email-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="confirm-poc">
                {t.confirmPocPhone}
              </label>
              <input
                id="confirm-poc"
                type="tel"
                value={draft.point_of_contact_phone}
                onChange={set("point_of_contact_phone")}
                placeholder="(407) 555-0101"
                className={inputCls}
                data-testid="confirm-details-poc-input"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[#EDE5D4] bg-[#FAF7F0] px-5 py-3">
          <button
            type="button"
            onClick={onSnooze}
            disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] transition-colors hover:bg-[#EDE5D4] disabled:opacity-60"
            data-testid="confirm-details-later"
          >
            {t.confirmLater}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] px-5 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#111A2E] disabled:opacity-60"
            data-testid="confirm-details-save"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? t.confirmSaving : t.confirmSave}
          </button>
        </div>
      </div>
    </div>
  );
}
